import { Box, HStack, Text } from "@chakra-ui/layout";
import { Button, Checkbox, IconButton, Tooltip } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import type { TaskRenderColumn } from "@src/components/modules/resource-planning/tasks/listing/builders";
import { TaskRowItem } from "@src/components/modules/resource-planning/tasks/listing/components/row-item";
import type { TaskModel } from "@src/components/modules/resource-planning/tasks/listing/models";
import { Icon } from "@src/components/ui-kit/Icon";
import { can } from "@src/utils/components/permissions";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import { Fragment } from "react";

type TaskRowProps = {
  task: TaskModel;
  taskColumns: TaskRenderColumn[];
  showParentTaskButton: boolean;
};

export const TASK_ROW_HEIGHT = 42;

export const TaskRow = observer(function TaskRow({
  task,
  taskColumns,
  showParentTaskButton = false,
}: TaskRowProps) {
  return (
    <Fragment>
      <TaskRowContent
        task={task}
        showParentTaskButton={showParentTaskButton}
        taskColumns={taskColumns}
      />
      {!showParentTaskButton &&
        task.isOpen.value &&
        task.subtasks?.map((subtask) => (
          <TaskRowContent
            key={subtask.id}
            task={subtask}
            showParentTaskButton={showParentTaskButton}
            taskColumns={taskColumns}
          />
        ))}
    </Fragment>
  );
});

type TaskRowContentProps = TaskRowProps;
const TaskRowContent = observer(function TaskRowContent({
  task,
  taskColumns,
  showParentTaskButton,
}: TaskRowContentProps) {
  const { tasksListingStore: store, authStore } = useStore();

  if (!store.columnsStore.titleColumn) return null;

  return (
    <HStack
      alignItems="center"
      pr="4"
      borderBottom="1px"
      borderColor="gray.200"
      bg="white"
      minW="fit-content"
      _hover={{ bg: "gray.50" }}
      minH={`${TASK_ROW_HEIGHT}px`}
      onClick={() => store.handleTaskClick(task)}
      cursor="pointer"
      role="group"
      spacing="0"
    >
      {/* Task Name + Subtasks Toggle - Sticky */}
      <TaskRowItem
        position="sticky"
        isReorderable={false}
        left="0"
        zIndex="1"
        bg="inherit"
        pl="4"
        h="40px"
        w={`${store.columnsStore.titleColumn.width}px`}
        minW={`${store.columnsStore.titleColumn.width}px`}
        maxW={`${store.columnsStore.titleColumn.width}px`}
        borderRight="1px"
        borderRightColor={
          store.horizontallyScrolled ? "gray.200" : "transparent"
        }
      >
        <HStack
          h="full"
          alignItems="center"
          pl={task.isSubtask && !showParentTaskButton ? "8" : "0"}
          w="full"
          pr="2"
          spacing="2"
        >
          {authStore.isInternalUser && (
            <Box
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                store.bulkActionsStore.handleSelect(task.id, task.subtaskIds);
              }}
            >
              <Checkbox
                isChecked={store.bulkActionsStore.isSelected(task.id)}
                isIndeterminate={store.bulkActionsStore.isIndeterminate(
                  task.id,
                  task.subtaskIds,
                )}
              />
            </Box>
          )}
          <Tooltip label={task.name} placement="top">
            <Text
              color="inherit"
              fontWeight="medium"
              textOverflow="ellipsis"
              noOfLines={1}
            >
              {task.name}
            </Text>
          </Tooltip>
          {task.showNonBillableIcon && (
            <Tooltip label={t`Non-billable`} shouldWrapChildren>
              <Icon name="non-billable" />
            </Tooltip>
          )}
          {!showParentTaskButton && task.showSubtasksToggle && (
            <Tooltip
              label={task.isOpen.value ? t`Hide subtasks` : t`Show subtasks`}
              placement="top"
            >
              <IconButton
                aria-label={t`Show sub tasks toggle`}
                colorScheme="grey"
                icon={<Icon name="git-branch-01" />}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  task.isOpen.toggle();
                }}
                size="sm"
                variant="ghost"
              />
            </Tooltip>
          )}

          {task.isSubtask && authStore.isInternalUser
            ? showParentTaskButton &&
              task.parent?.id && (
                <Tooltip
                  label={
                    <Text>
                      <Trans>Click to open parent task:</Trans>
                      <br />
                      {task.parent.id}&nbsp;-&nbsp;{task.parent.name}
                    </Text>
                  }
                  placement="top"
                >
                  <IconButton
                    aria-label={t`Open parent task`}
                    colorScheme="grey"
                    icon={<Icon name="git-branch-01" />}
                    onClick={(e) => {
                      if (!task.parent) return;
                      e.preventDefault();
                      e.stopPropagation();
                      store.handleTaskClick(task.parent);
                    }}
                    size="sm"
                    variant="ghost"
                  />
                </Tooltip>
              )
            : can("task_create_all") && (
                <Button
                  display="none"
                  minW="fit-content"
                  fontWeight="medium"
                  _hover={{
                    "&>div>p": {
                      display: "block",
                      ml: "",
                    },
                  }}
                  _groupHover={{ display: "inline-flex" }}
                  colorScheme="purple"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    task.createSubtask();
                  }}
                  size="xs"
                  variant="outline"
                >
                  <HStack spacing="0">
                    <Icon name="plus" boxSize="3" />
                    <Text
                      display="none"
                      fontSize="inherit"
                      fontWeight="inherit"
                    >
                      <Trans>Subtask</Trans>
                    </Text>
                  </HStack>
                </Button>
              )}
        </HStack>
      </TaskRowItem>

      {taskColumns.map(({ Component, key, width }, index, array) => (
        <Component
          key={`task-row-column-${key}`}
          task={task}
          gridItemProps={{
            w: `${width}px`,
            minW: `${width}px`,
            maxW: `${width}px`,
            overflow: "hidden",
            textOverflow: "clip",
            borderLeft: index === 0 ? "1px" : undefined,
            borderLeftColor: index === 0 ? "gray.200" : undefined,
            borderRightColor:
              index < array.length - 1 ? "gray.200" : "transparent",
          }}
        />
      ))}
    </HStack>
  );
});
